import React, { useRef } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import { EMPTY_IP_OCTETS } from '../../constants';
import type { AccountFormValues, IpOctets } from '../../types/account';

const OCTET_FIELDS: Array<keyof IpOctets> = ['octet1', 'octet2', 'octet3', 'octet4'];

export const IpOctetInput: React.FC = () => {
  const translate = useTranslate();
  const { register, setValue } = useFormContext<AccountFormValues>();
  const ips = useWatch<AccountFormValues, 'whiteListedIps'>({ name: 'whiteListedIps' }) ?? [];
  const refs = useRef<Array<Array<HTMLInputElement | null>>>([]);

  const addIp = () => {
    setValue('whiteListedIps', [...ips, { ...EMPTY_IP_OCTETS }], {
      shouldDirty: true,
    });
  };

  const removeIp = (index: number) => {
    if (ips.length <= 1) return;
    setValue(
      'whiteListedIps',
      ips.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number,
    octetIndex: number
  ) => {
    if (event.key === '.') {
      event.preventDefault();
      const next = refs.current[rowIndex]?.[octetIndex + 1];
      if (next) next.focus();
    }
  };

  return (
    <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
      <Box
        component="button"
        type="button"
        onClick={addIp}
        sx={{
          all: 'unset',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          px: 2,
          py: 1,
          borderRadius: 1,
          cursor: 'pointer',
          fontWeight: 600,
          mb: 2,
          display: 'inline-block',
        }}
      >
        + {translate('resources.accounts.actions.addIpField')}
      </Box>
      {ips.map((_, rowIndex) => {
        refs.current[rowIndex] ??= [];
        return (
          <Box
            key={rowIndex}
            sx={{
              bgcolor: 'background.paper',
              p: { xs: 1, sm: 2 },
              mb: 1,
              display: 'flex',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography sx={{ minWidth: { xs: 0, sm: '6.25rem' }, width: { xs: '100%', sm: 'auto' }, fontWeight: 500 }}>
              {translate('resources.accounts.fields.ipAddress')} {rowIndex + 1}
            </Typography>
            {OCTET_FIELDS.map((field, octetIndex) => (
              <React.Fragment key={field}>
                <TextField
                  {...register(`whiteListedIps.${rowIndex}.${field}` as const)}
                  inputRef={(el: HTMLInputElement | null) => {
                    refs.current[rowIndex][octetIndex] = el;
                  }}
                  type="number"
                  size="small"
                  inputProps={{ min: 0, max: 255, maxLength: 3 }}
                  onKeyDown={(e) =>
                    handleKeyDown(
                      e as React.KeyboardEvent<HTMLInputElement>,
                      rowIndex,
                      octetIndex
                    )
                  }
                  sx={{ width: 72 }}
                />
                {octetIndex < 3 && (
                  <Typography sx={{ fontWeight: 700, px: 0.5 }}>.</Typography>
                )}
              </React.Fragment>
            ))}
            {ips.length > 1 && (
              <IconButton
                size="small"
                onClick={() => removeIp(rowIndex)}
                aria-label="remove"
                sx={{ ml: 1 }}
              >
                <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default IpOctetInput;
